import ToneCard from '@/components/ui/ToneCard/ToneCard';
import { AudioLines, Bookmark, Download, Globe, Lock } from 'lucide-react';
import Link from 'next/link';
import Paginator from '../Paginator/Paginator';
import { ToneCountWithUser } from '@/utils/helpers';
import LoadingDots from '../LoadingDots/LoadingDots';
import { ToneCardActions } from '../ToneCard/ToneCardActions';
import { ArchitectureFilter } from '@/utils/constants';
import { useTranslations } from 'next-intl';

interface Props {
  tones: ToneCountWithUser[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  tab: string;
  ownProfile: boolean;
  loading?: boolean;
  isApiSelect?: boolean;
  /** Architecture context for displayed model counts (Select OAuth flow only). */
  architecture?: ArchitectureFilter | null;
}

const Tones = ({
  tones,
  totalCount,
  page,
  pageSize,
  totalPages,
  tab,
  ownProfile,
  loading,
  isApiSelect = false,
  architecture = null,
}: Props) => {
  const t = useTranslations('profile');
  return (
    <div className={'flex flex-col flex-1 gap-8 relative'}>
      {tab === 'favorites' && (
        <div
          className={`flex items-center ${tones?.length ? 'justify-start' : 'justify-center'} max-w-[420px] mt-[-16px]`}
        >
          <div className="flex flex-row gap-4 items-center justify-center">
            <Lock size={20} className="text-zinc-400" />
            <p className="text-zinc-400">Only you can see your favorites.</p>
          </div>
        </div>
      )}
      {tab === 'downloads' && (
        <div
          className={`flex items-center ${tones?.length ? 'justify-start' : 'justify-center'} max-w-[420px] mt-[-16px]`}
        >
          <div className="flex flex-row gap-4 items-center justify-center">
            <Lock size={20} className="text-zinc-400" />
            <p className="text-zinc-400">Only you can see your recently used.</p>
          </div>
        </div>
      )}
      {tones?.map((tone) => (
        <ToneCard
          key={tone.id}
          tone={tone}
          isOwner={ownProfile}
          from="profile_page"
          action={
            ownProfile && tab === 'created' && !isApiSelect ? (
              <ToneCardActions tone={tone} />
            ) : undefined
          }
          isApiSelect={isApiSelect}
          architecture={architecture}
        />
      ))}

      {!loading && !tones?.length && tab === 'created' && !!ownProfile && (
        <div className="flex items-center justify-center max-w-[420px]">
          <div className="flex flex-col max-w-[320px] items-center justify-center px-8 py-8 mt-8 text-center bg-zinc-900 rounded-xl">
            <div className="mb-4">
              <AudioLines className="flex-shrink-0 text-zinc-400" size={40} />
            </div>
            <h2 className="text-base font-semibold text-white mb-2">
              <Link href={'/capture'}>Create Your First Tone</Link>
            </h2>
            <p className="text-zinc-400">
              Capture a model of your gear or upload NAM files and IR's.
            </p>
          </div>
        </div>
      )}
      {!tones?.length && tab === 'created' && !ownProfile && (
        <div className="flex items-center justify-center max-w-[420px]">
          <div className="flex flex-col max-w-[320px] items-center justify-center px-8 py-8 mt-8 text-center bg-zinc-900 rounded-xl">
            <div className="mb-4">
              <AudioLines className="flex-shrink-0 text-zinc-400" size={40} />
            </div>
            <h2 className="text-base font-semibold text-white">
              <Link href={'/'}>{t('noPublicTones')}</Link>
            </h2>
          </div>
        </div>
      )}
      {!tones?.length && tab === 'favorites' && (
        <div className="flex flex-col items-center justify-center max-w-[420px]">
          <div className="flex flex-col max-w-[320px] items-center justify-center px-8 py-8 mt-6 text-center bg-zinc-900 rounded-xl">
            <div className="mb-4">
              <Bookmark className="flex-shrink-0 text-zinc-400" size={40} />
            </div>
            <h2 className="text-base font-semibold text-white mb-2">
              <Link href={'/'}>Favorites</Link>
            </h2>
            <p className="text-zinc-400">Save a private library of your favorite tones.</p>
          </div>
        </div>
      )}
      {!loading && !tones?.length && tab === 'downloads' && (
        <div className="flex flex-col items-center justify-center max-w-[420px]">
          <div className="flex flex-col max-w-[320px] items-center justify-center px-8 py-8 mt-6 text-center bg-zinc-900 rounded-xl">
            <div className="mb-4">
              <Download className="flex-shrink-0 text-zinc-400" size={40} />
            </div>
            <h2 className="text-base font-semibold text-white mb-2">
              <Link href={'/'}>Downloads</Link>
            </h2>
            <p className="text-zinc-400">Tones you download will show up here.</p>
          </div>
        </div>
      )}
      {totalCount > pageSize && (
        <Paginator
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
        />
      )}
      {loading && (
        <div className="absolute inset-0 flex items-start justify-center bg-black/50 pt-16 backdrop-blur-sm absolute inset-0 z-20">
          <LoadingDots />
        </div>
      )}
    </div>
  );
};

export { Tones };
